import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { CardPreview } from "./CardPreview";
import "./CardPreviewContainer.less";

interface CardPreviewProps {
  status?:
    | "photo-pending"
    | "photo-review"
    | "photo-rejected"
    | "questionnaire-topic"
    | "questionnaire-input"
    | "sent"
    | "default";
  uploadedPhotoUrl: string;
  surveyCompleted?: boolean;
}

const CardPreviewContainer = forwardRef<{ animateCardPreview: () => void }, CardPreviewProps>(
  ({ status, uploadedPhotoUrl, surveyCompleted = false }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [tempStatus, setTempStatus] = useState<"questionnaire-input" | null>(null);

    // Expose animateCardPreview function to parent component
    useImperativeHandle(ref, () => ({
      animateCardPreview: () => {
        // We need to fully reset, then trigger the animation again
        // First change to questionnaire-input to reset card positions
        setTempStatus("questionnaire-input");

        // Then change back to sent status after a longer delay
        // to ensure the card positions are fully reset
        setTimeout(() => {
          setTempStatus(null);
        }, 300); // Longer delay to ensure the questionnaire state is fully applied
      }
    }));

    // Use ResizeObserver to track container width changes
    useEffect(() => {
      // Store the current value of the ref to use it in the cleanup function
      const currentContainerRef = containerRef.current;

      if (!currentContainerRef) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(currentContainerRef);

      // Set initial width
      setContainerWidth(currentContainerRef.offsetWidth);

      return () => {
        if (currentContainerRef) {
          resizeObserver.unobserve(currentContainerRef);
        }
        resizeObserver.disconnect();
      };
    }, []);

    // Determine content based on status.
    let content: React.ReactNode;

    // Use tempStatus for animation or regular status
    const displayStatus = tempStatus || status;

    // These are listed in the order in which they occur in the flow.
    switch (displayStatus) {
      case "photo-pending":
        content = <CardPreview status={displayStatus} containerWidth={containerWidth} />;
        break;
      case "photo-review":
        content = (
          <CardPreview
            status={displayStatus}
            imageUrl={uploadedPhotoUrl}
            containerWidth={containerWidth}
          />
        );
        break;
      case "photo-rejected":
        content = <CardPreview status={displayStatus} containerWidth={containerWidth} />;
        break;
      case "questionnaire-topic":
      case "questionnaire-input":
        content = (
          <CardPreview
            status="questionnaire"
            imageUrl={uploadedPhotoUrl}
            containerWidth={containerWidth}
          />
        );
        break;
      case "sent":
        content = (
          <CardPreview
            status={displayStatus}
            imageUrl={uploadedPhotoUrl}
            surveyCompleted={surveyCompleted}
            containerWidth={containerWidth}
          />
        );
        break;
      default:
        return null;
    }

    return (
      <div className="card-preview-container__wrapper" ref={containerRef}>
        {content}
      </div>
    );
  }
);

export default CardPreviewContainer;
